import { useState, useEffect } from "react";
import { Calendar } from "primereact/calendar";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import { InputText } from "primereact/inputtext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";

export interface AssessmentDateModeProps {
  startDate: Date;
  endDate: Date | null;
  daysBetween: number;
  handleEndDateChange: (e: { value: Date | null }) => void;
  isDynamic?: boolean;
  toggleDynamicMode: (isDynamic: boolean) => void;
}

const AssessmentDateMode = ({
  startDate,
  endDate,
  daysBetween,
  handleEndDateChange,
  isDynamic,
  toggleDynamicMode,
}: AssessmentDateModeProps) => {
  const [dynamicDateValue, setDynamicDateValue] = useState<string | number>(daysBetween ?? 0);

  useEffect(() => {
    if (isDynamic) {
      setDynamicDateValue(daysBetween.toString());
    }
  }, [daysBetween, isDynamic]);

  const handleDynamicDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let daysToAdd: string | number = e.target.value;
    if (daysToAdd === "") daysToAdd = 0;
    setDynamicDateValue(daysToAdd);

    const totalDays = parseInt(String(daysToAdd), 10) || 0;
    const inclusiveEndOffset = totalDays > 0 ? totalDays - 1 : 0;
    const newEndDate = new Date(startDate);
    newEndDate.setDate(newEndDate.getDate() + inclusiveEndOffset);
    handleEndDateChange({ value: newEndDate });
  };

  return (
    <fieldset disabled={!startDate} className="w-full">
      <p className="mb-2">Time Frame (required):</p>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <RadioButton
            inputId="fixed-date"
            name="dateMode"
            value="fixed"
            checked={!isDynamic}
            onChange={() => toggleDynamicMode(false)}
          />
          <label htmlFor="fixed-date">Fixed Date</label>
          <FontAwesomeIcon
            id="fixed-date-tooltip"
            style={{ marginLeft: "8px", cursor: "pointer" }}
            size="sm"
            icon={faQuestionCircle}
          />
          <Tooltip target="#fixed-date-tooltip" mouseTrack mouseTrackLeft={10}>
            Users added after the start date will still have the same end date
          </Tooltip>
        </div>
        <fieldset disabled={!!isDynamic} className="pl-6">
          <div className="flex flex-col gap-1">
            <label htmlFor="end-date" className="block">
              End Date
            </label>
            <Calendar
              className="w-full max-w-64"
              inputId="end-date"
              name="end-date"
              value={isDynamic ? null : endDate}
              minDate={startDate ? new Date(startDate) : new Date()}
              placeholder="Select end date"
              onChange={e => handleEndDateChange({ value: (e.value as Date | null) ?? null })}
              showIcon
            />
            <span className="body-sm">{isDynamic ? "" : `${daysBetween} days`}</span>
          </div>
        </fieldset>
        <div className="flex items-center gap-2">
          <RadioButton
            inputId="dynamic-date"
            name="dateMode"
            value="dynamic"
            checked={!!isDynamic}
            onChange={() => toggleDynamicMode(true)}
          />
          <label htmlFor="dynamic-date-input">Dynamic Date</label>
          <FontAwesomeIcon
            id="dynamic-date-tooltip"
            style={{ marginLeft: "8px", cursor: "pointer" }}
            size="sm"
            icon={faQuestionCircle}
          />
          <Tooltip target="#dynamic-date-tooltip" mouseTrack mouseTrackLeft={10}>
            Users will have the same amount of time to complete the assignment
          </Tooltip>
        </div>
        <fieldset disabled={!isDynamic} className="pl-6">
          <div className="flex flex-col gap-1 w-full max-w-64">
            <InputText
              id="dynamic-date-input"
              name="dynamic-date-input"
              disabled={!isDynamic}
              value={isDynamic ? String(dynamicDateValue) : ""}
              onChange={handleDynamicDateChange}
              keyfilter="int"
              aria-label="Assignment duration in days"
              className="w-full"
            />
            <p className="body-sm">Assignment Duration (days)</p>
          </div>
        </fieldset>
      </div>
    </fieldset>
  );
};

export default AssessmentDateMode;
